// src/shared/modules/menu/Menu.tsx

import React, { useEffect, useState } from "react";
import LoadingScreen from "../../../shared/components/LoadingScreen";
import { BUSINESS_MENUS } from "./menuConfig";
import { useBusinessSettings } from "./hooks/useBusinessSettings";
import { useActiveMenu } from "./hooks/useActiveMenu";
import MenuItem from "./components/MenuItem";
import MobileMenu from "./components/MobileMenu";

import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import ViewListIcon from "@mui/icons-material/ViewList";
import ImportContactsIcon from "@mui/icons-material/ImportContacts";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import HomeIcon from "@mui/icons-material/Home";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import { useUser } from "src/shared/context/UserContext";
import EventNoteIcon from "@mui/icons-material/EventNote";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import { hasPermission } from "src/shared/components/PermissionRoute";
import { useUserTheme } from "src/shared/utils/useUserTheme";
import FolderIcon from "@mui/icons-material/Folder";
import iconMap from "src/assets/icons/Icons";
import NotificationComponent from "src/shared/socket/NotificationComponent";

function Menu() {
  const [clickMenu, setClickMenu] = useState(false);
  const [overlayMenu, setOverlayMenu] = useState(true);
  const { DraftsTwoToneIcon } = iconMap;

  const { businessType, isEmployee, loading } = useBusinessSettings();
  const { user, userConfigration } = useUser();
  const { sidebarThemeClass } = useUserTheme();

  useEffect(() => {
    const menuShow = sessionStorage.getItem("overlayMenu");
    if (menuShow) {
      setClickMenu(menuShow === "true");
    } else {
      setClickMenu(false);
    }
  }, []);

  const menuConfig = BUSINESS_MENUS[businessType] || {};
  const { activeMenu, handleMenuClick } = useActiveMenu(
    businessType,
    isEmployee,
    menuConfig
  );

  if (!user?.businessType) return null;

  // ✅ Fixed: pass raw value (no "=== true" inside)
  const toBoolean = (value: any) => value === true || value === "true";
  const isCreditControlEnabled = toBoolean(userConfigration?.creditControlEnabled);

  const menuItems = [
    {
      title: "Home",
      path: isEmployee ? menuConfig.employeeHomePath : menuConfig.homePath,
      Icon: HomeIcon,
      notify: false,
    },
    {
      title: "Dashboard",
      path: isEmployee
        ? menuConfig.employeeDashboardPath ?? menuConfig.dashboardPath
        : menuConfig.dashboardPath,
      Icon: DashboardRoundedIcon,
      permission: "dashboard_view",
      notify: false,
    },
    {
      title: "Orders",
      path: isEmployee
        ? menuConfig.employeeOrderPath ?? menuConfig.orderPath
        : menuConfig.orderPath,
      Icon: ViewListIcon,
      permission: "order_view",
      notify: false,
    },
    {
      title: "Ledgers",
      path: isEmployee ? menuConfig.employeeLedgerPath : menuConfig.ledgerPath,
      Icon: ImportContactsIcon,
      permission: "customer_view",
      notify: false,
    },
    {
      title: "Drafts",
      path: menuConfig.Draft,
      Icon: DraftsTwoToneIcon,
      permission: "",
      notify: true,
      requiresCreditControl: true,
    },
    {
      title: "Reports",
      path: menuConfig.DailyReport,
      Icon: EventNoteIcon,
      permission: "report_view",
      notify: false,
    },
    {
      title: "Running Order",
      path: menuConfig.runningOrder,
      Icon: DirectionsRunIcon,
      permission: "order_view",
      notify: false,
    },
    {
      title: "Catalog",
      path: menuConfig.Catalog,
      Icon: FolderIcon,
      permission: "catalog_view",
      notify: false,
    },
    {
      title: "Admin",
      path: menuConfig.settingsPath,
      Icon: SettingsSuggestIcon,
      permission: "management_view",
      notify: false,
    },
    {
      title: "Configurations",
      path: menuConfig.Configurations,
      Icon: ManageAccountsIcon,
      permission: "setting_view",
      notify: false,
    },
    // {
    //   title: "Tools",
    //   path: menuConfig.toolsPath,
    //   Icon: CalculateIcon,
    //   permission: "tools_view",
    // },
  ];

const renderMenuItems = (compact: boolean) =>
  menuItems
    .filter(({ permission, path, requiresCreditControl }) => {
      if (!path) return false;
      if (requiresCreditControl && !isCreditControlEnabled) return false;
      if (user?.role?.[0] === "Owner") return true;

      const permissions =
        user?.permissions && !Array.isArray(user.permissions)
          ? user.permissions
          : {};

      return hasPermission(
        permissions as Record<string, string[]>,
        permission
      );
    })
    .map(({ title, path, Icon, notify }) => (
      <div className="flex relative" key={title}>
        <MenuItem
          title={title}
          path={path}
          Icon={Icon}
          compact={compact}
          isActive={activeMenu === title}
          onClick={handleMenuClick}
        />
        {notify && <NotificationComponent />}
      </div>
    ));

  const dataSet = (value: boolean) => {
    sessionStorage.setItem("overlayMenu", String(value));
    setClickMenu(value);
  };

  return (
    <div
      className={`pr-2 rounded-r-lg border ${sidebarThemeClass}`}
      style={{ height: "92vh", zIndex: 0 }}
    >
      <MobileMenu
        overlayMenu={overlayMenu}
        setOverlayMenu={setOverlayMenu}
        clickmenu={clickMenu}
        setClickMenu={dataSet}
      >
        {renderMenuItems(clickMenu)}
      </MobileMenu>

      {loading && <LoadingScreen />}
    </div>
  );
}

export default Menu;